import { useImagesFastCache } from "@design-lab/entities/artwork/model/useImagesFastCache";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import type { aiImages } from "common/utils/models";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import { ImagePlaceholder } from "./ImagePlaceholder";

export type GeneratedImage = {
  id: number;
  imageSrc: string;
  imageAlt: string;
  style?: aiImages.generate.AiImageStyle;
  provider?: string;
};

// Sample image paths (without domain)
const SAMPLE_IMAGE_PATHS = [
  "2/gen/20251008_164354_2_ad34f512-617c-4ff1-9ebc-6ea517f32518.png",
  "2/gen/20251008_164354_2_ebfe5b39-39f0-4981-98c5-34e06b348b9c.png",
  "2/gen/20251008_164354_3_20093a3a-f987-4759-a533-11eff1d66463.png",
  "2/gen/20251008_164354_3_fdb8da21-beb7-4982-af2e-064ec2e67517.png",
  "2/gen/20251008_164354_4_2b45ad54-9cea-4ae9-9bc2-216dac88e633.png",
  "2/gen/20251008_164354_4_7600406c-b3e1-423d-9d23-1dc995d270d4.png",
  "2/gen/20251008_164354_4_f0c22759-6844-447b-901e-393e9cf66b9e.png",
  "2/gen/20251008_164354_5_2b45ad54-9cea-4ae9-9bc2-216dac88e633.png",
  "2/gen/20251008_164354_5_4998f0e1-6604-46ee-956c-a62773744640.png",
  "2/gen/20251008_164354_5_fcd1b979-6d1d-42e3-bf0f-3d9782b48810.png",
  "2/gen/20251008_164354_6_146bbae4-8b2e-4e01-9f49-8e419b5fee93.png",
  "2/gen/20251008_164354_6_2353405a-e782-4a09-8225-eaa285ddda41.png",
  "2/gen/20251008_164354_6_4998f0e1-6604-46ee-956c-a62773744640.png",
  "2/gen/20251008_164354_7_1edaf99d-2586-49ed-9fd4-54e51fc8a231.png",
  "2/gen/20251008_164354_7_5d4ff2f1-fccd-4397-a111-cf24516e3dd3.png",
  "2/gen/20251008_164354_8_35a077a5-f470-442a-8f91-aba7f618fbda.png",
  "2/gen/20251008_164354_8_497492b4-acc7-4e5e-98ab-22993469fbf0.png",
  "2/gen/20251008_164354_8_5d4ff2f1-fccd-4397-a111-cf24516e3dd3.png",
  "2/gen/20251008_164354_9_d41ab5ed-7d4c-4610-9c43-c3a9aeb75eb6.jpg"
];

// Build full URLs dynamically
export const SAMPLE_IMAGE_URLS = SAMPLE_IMAGE_PATHS.map(path => remoteImageStaticUrl(path));

// Function to randomly sample 9 images from the list
export function sampleRandomImages(imageUrls: string[], count: number = 9): GeneratedImage[] {
  const shuffled = [...imageUrls].sort(() => 0.5 - Math.random());
  const selected = shuffled.slice(0, count);

  return selected.map((url, index) => ({
    id: index + 1000,
    imageSrc: url,
    imageAlt: `Sample artwork ${index + 1}`
  }));
}

export type AiImageGalleryProps = {
  images: GeneratedImage[];
  isGenerating: boolean;
  selectedImageId: number | undefined;
  uploadingImageId?: number | undefined;
  startTime: number | undefined;
  onImageSelect: (imageId: number) => void;
  onImageEdit?: (imageId: number) => void;
};

export function AiImageGallery({
  images,
  isGenerating,
  selectedImageId,
  uploadingImageId,
  startTime,
  onImageSelect,
  onImageEdit
}: AiImageGalleryProps) {
  const { isDesktop } = useResponsive();

  // Cache all gallery images for instant display
  const imageUrls = images.map(img => img.imageSrc);
  const cachedImages = useImagesFastCache(imageUrls);

  // Create a map for quick lookup of cached images
  const cachedImageMap = new Map(
    cachedImages.map((cached, idx) => [imageUrls[idx], cached?.base64])
  );

  const totalItems = 9;
  const placeholdersNeeded = isGenerating ? Math.max(0, totalItems - images.length) : 0;
  const items = [
    ...images.map(img => ({ type: "image" as const, data: img })),
    ...Array.from({ length: placeholdersNeeded }, (_, i) => ({
      type: "placeholder" as const,
      key: `loading-${i}`
    }))
  ];

  if (!isDesktop) {
    return (
      <div className="flex h-full items-center gap-2 overflow-x-auto overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map(item => (
          <div
            key={(item as any).data?.id ?? (item as any).key}
            className="aspect-square h-full max-h-64 flex-none"
          >
            {item.type === "image" ? (
              <ImagePlaceholder
                imageSrc={cachedImageMap.get(item.data.imageSrc) || item.data.imageSrc}
                imageAlt={item.data.imageAlt}
                isSelected={selectedImageId === item.data.id}
                isUploading={uploadingImageId === item.data.id}
                startTime={uploadingImageId === item.data.id ? startTime : undefined}
                crossOrigin="anonymous"
                {...(uploadingImageId === undefined && {
                  onSelect: () => onImageSelect(item.data.id),
                  onEdit: () => onImageEdit?.(item.data.id),
                  showEditButton: !!item.data.imageSrc && !!onImageEdit
                })}
              />
            ) : (
              <ImagePlaceholder
                isLoading={isGenerating}
                startTime={startTime}
                crossOrigin="anonymous"
              />
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <h2 className="mb-4 text-base font-semibold leading-6 text-gray-600">AI Image Gallery</h2>

      <div className="min-h-0 overflow-auto">
        <div className="grid w-[504px] grid-cols-3 gap-3">
          {items.map((item, idx) => (
            <div
              key={(item as any).data?.id ?? (item as any).key ?? idx}
              className="aspect-square h-40 w-40"
            >
              {item.type === "image" ? (
                <ImagePlaceholder
                  imageSrc={cachedImageMap.get(item.data.imageSrc) || item.data.imageSrc}
                  imageAlt={item.data.imageAlt}
                  isSelected={selectedImageId === item.data.id}
                  isUploading={uploadingImageId === item.data.id}
                  startTime={uploadingImageId === item.data.id ? startTime : undefined}
                  crossOrigin="anonymous"
                  {...(uploadingImageId === undefined && {
                    onSelect: () => onImageSelect(item.data.id),
                    onEdit: () => onImageEdit?.(item.data.id),
                    showEditButton: !!item.data.imageSrc && !!onImageEdit
                  })}
                  hoverAlignment="center"
                />
              ) : (
                <ImagePlaceholder
                  isLoading={isGenerating}
                  startTime={startTime}
                  crossOrigin="anonymous"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
